import { useEffect, useState } from 'react'
import { CardAPI, type CardResponse } from '@certi/shared/api/payments/CardAPI'
import { useAuth } from '@/hooks/useAuth'
import { StripeProvider } from '../payments/StripeProvider'
import { AddCardForm } from '../payments/AddCardForm'
import { FaCcVisa, FaCcMastercard, FaCcAmex, FaCcDiscover } from 'react-icons/fa'
import { MdCreditCard } from 'react-icons/md'
import { isStripeConfigured } from '@/config/stripe'

function CardsSectionContent() {
  const { accessToken, session, isHydrated } = useAuth()
  const [cards, setCards] = useState<CardResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [paymentSystemUnavailable, setPaymentSystemUnavailable] = useState(false)

  // Derive token from accessToken or session (fallback for rehydration)
  const token = accessToken ?? session?.accessToken
  const stripeAvailable = isStripeConfigured()
  const showPaymentUnavailable = paymentSystemUnavailable || !stripeAvailable

  const loadCards = async () => {
    // Wait for hydration before checking token
    if (!isHydrated) return

    if (!token) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    setPaymentSystemUnavailable(false)
    try {
      const data = await CardAPI.listCards(token)
      setCards(data)
      setPaymentSystemUnavailable(false)
    } catch (err: any) {
      console.error('Failed to load cards:', err)
      // Check if it's a 500 error (payment system unavailable)
      if (err.message && (err.message.includes('500') || err.message.includes('Internal Server Error'))) {
        setPaymentSystemUnavailable(true)
      }
      setError(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCards()
  }, [token, isHydrated])

  const handleAddSuccess = async () => {
    setShowAddForm(false)
    await loadCards()
  }

  const handleSetDefault = async (paymentMethodId: string) => {
    if (!token) return

    setActionLoading(paymentMethodId)
    setError(null)
    try {
      await CardAPI.setDefaultCard(paymentMethodId, token)
      await loadCards()
    } catch (err: any) {
      console.error('Failed to set default card:', err)
      setError('Unable to set default card. Please try again.')
    } finally {
      setActionLoading(null)
    }
  }

  const handleDeleteClick = (paymentMethodId: string) => {
    setDeleteConfirmId(paymentMethodId)
  }

  const handleCancelDelete = () => {
    setDeleteConfirmId(null)
  }

  const handleConfirmDelete = async () => {
    if (!token || !deleteConfirmId) return

    setActionLoading(deleteConfirmId)
    setError(null)
    try {
      await CardAPI.deleteCard(deleteConfirmId, token)
      await loadCards()
      setDeleteConfirmId(null)
    } catch (err: any) {
      console.error('Failed to delete card:', err)
      setError('Unable to remove card. Please try again.')
    } finally {
      setActionLoading(null)
    }
  }

  const getCardBrandIcon = (brand: string) => {
    const brandLower = brand.toLowerCase()
    const iconClassName = "text-4xl"

    switch (brandLower) {
      case 'visa':
        return <FaCcVisa className={`${iconClassName} text-blue-600`} />
      case 'mastercard':
        return <FaCcMastercard className={`${iconClassName} text-red-600`} />
      case 'amex':
        return <FaCcAmex className={`${iconClassName} text-blue-500`} />
      case 'discover':
        return <FaCcDiscover className={`${iconClassName} text-orange-500`} />
      default:
        return <MdCreditCard className={`${iconClassName} text-gray-600`} />
    }
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Loading cards...</p>
      </div>
    )
  }

  if (!token && isHydrated) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Please sign in to manage payment methods</p>
      </div>
    )
  }

  if (showAddForm) {
    if (!token) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-600">Please sign in to add a payment method</p>
        </div>
      )
    }

    return (
      <div>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Add New Card</h2>
          <p className="text-gray-600 mt-1">
            Add a card to allow a temporary reservation hold when needed
          </p>
        </div>
        {!stripeAvailable ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm font-medium text-yellow-800">Payment System Temporarily Unavailable</p>
            <p className="text-sm text-yellow-700 mt-1">
              Card setup is not configured for this environment. Please try again later.
            </p>
            <button
              type="button"
              onClick={() => setShowAddForm(false)}
              className="btn btn-secondary mt-4 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            >
              Back to payment methods
            </button>
          </div>
        ) : (
          <AddCardForm
            accessToken={token}
            onSuccess={handleAddSuccess}
            onCancel={() => setShowAddForm(false)}
          />
        )}
      </div>
    )
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Payment Methods</h2>

      {showPaymentUnavailable && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-sm font-medium text-yellow-800">Payment System Temporarily Unavailable</p>
              <p className="text-sm text-yellow-700 mt-1">
                {!stripeAvailable
                  ? 'Card setup is not configured for this environment. Please try again later.'
                  : "We're experiencing technical difficulties with our payment provider. Please try again later or contact support if the issue persists."}
              </p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {cards.length === 0 ? (
        <div className="text-center py-12">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            You have no saved cards yet
          </h3>
          <p className="text-gray-600 max-w-md mx-auto mb-6">
            Add a card to allow a temporary reservation hold when needed
          </p>
          <button
            onClick={() => setShowAddForm(true)}
            disabled={paymentSystemUnavailable || !stripeAvailable}
            className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add new card
          </button>
        </div>
      ) : (
        <div>
          <div className="mb-6 flex justify-center">
            <button
              onClick={() => setShowAddForm(true)}
              disabled={paymentSystemUnavailable || !stripeAvailable}
              className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add new card
            </button>
          </div>

          <div className="space-y-4">
            {cards.map((card) => (
              <div
                key={card.paymentMethodId}
                className="border border-gray-200 rounded-lg p-4 flex items-center justify-between hover:border-gray-300 transition-colors"
              >
                <div className="flex items-center gap-4">
                  {getCardBrandIcon(card.brand)}
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 capitalize">
                        {card.brand}
                      </span>
                      <span className="text-gray-600">•••• {card.last4}</span>
                    </div>
                    <p className="text-sm text-gray-500">
                      Expires {card.expMonth.toString().padStart(2, '0')}/
                      {card.expYear.toString().slice(-2)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {card.isDefault || cards.length === 1 ? (
                    <span
                      className="text-gray-500 px-3 py-2 text-sm font-medium cursor-default"
                    >
                      Default
                    </span>
                  ) : (
                    <button
                      onClick={() => handleSetDefault(card.paymentMethodId)}
                      disabled={actionLoading === card.paymentMethodId}
                      className="text-green-600 hover:text-green-800 px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading === card.paymentMethodId
                        ? 'Setting...'
                        : 'Set as default'}
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteClick(card.paymentMethodId)}
                    disabled={actionLoading === card.paymentMethodId}
                    className="text-red-600 hover:text-red-800 px-3 py-2 text-sm font-medium transition-colors"
                  >
                    {actionLoading === card.paymentMethodId ? 'Removing...' : 'Remove'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId !== null && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 m-4 w-full max-w-md">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Remove Card</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to remove this payment method? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleCancelDelete}
                disabled={actionLoading === deleteConfirmId}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={actionLoading === deleteConfirmId}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading === deleteConfirmId ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Payment Methods Section
 * Wrapped with StripeProvider to enable Stripe Elements
 */
export default function CardsSection() {
  return (
    <StripeProvider>
      <CardsSectionContent />
    </StripeProvider>
  )
}
